/**
 * modal.js — TekRevive unified modal and dialog system.
 * Accessible, branded replacement for browser native alert() and confirm().
 */
(function () {
  "use strict";

  const ICONS = {
    warning: `<svg width="22" height="22" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round" aria-hidden="true"><circle cx="12" cy="12" r="10"/><line x1="12" y1="8" x2="12" y2="12"/><line x1="12" y1="16" x2="12.01" y2="16"/></svg>`,
    error: `<svg width="22" height="22" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round" aria-hidden="true"><circle cx="12" cy="12" r="10"/><line x1="15" y1="9" x2="9" y2="15"/><line x1="9" y1="9" x2="15" y2="15"/></svg>`,
    info: `<svg width="22" height="22" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round" aria-hidden="true"><circle cx="12" cy="12" r="10"/><line x1="12" y1="16" x2="12" y2="12"/><line x1="12" y1="8" x2="12.01" y2="8"/></svg>`,
    success: `<svg width="22" height="22" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round" aria-hidden="true"><path d="M22 11.08V12a10 10 0 1 1-5.93-9.14"/><polyline points="22 4 12 14.01 9 11.01"/></svg>`,
    package: `<svg width="22" height="22" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round" aria-hidden="true"><path d="M12 22s8-4 8-10V5l-8-3-8 3v7c0 6 8 10 8 10z"/></svg>`,
    shield: `<svg width="22" height="22" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round" aria-hidden="true"><path d="M12 22s8-4 8-10V5l-8-3-8 3v7c0 6 8 10 8 10z"/></svg>`,
  };

  function escapeHtml(str) {
    if (!str) return "";
    return String(str)
      .replace(/&/g, "&amp;")
      .replace(/</g, "&lt;")
      .replace(/>/g, "&gt;")
      .replace(/"/g, "&quot;")
      .replace(/'/g, "&#039;");
  }

  function fmtMoney(cents) {
    try {
      return new Intl.NumberFormat("en-AU", { style: "currency", currency: "AUD", maximumFractionDigits: 0 }).format(cents / 100);
    } catch (e) {
      return "$" + Math.round(cents / 100);
    }
  }

  function detectTypeAndTitle(message, providedTitle, providedType) {
    const msg = String(message || "");
    let type = providedType;
    let title = providedTitle;

    if (!type) {
      if (/maximum|limit|already in your cart|available quantity/i.test(msg)) {
        type = "warning";
      } else if (/fail|error|couldn't|unable/i.test(msg)) {
        type = "error";
      } else if (/success|added|saved/i.test(msg)) {
        type = "success";
      } else {
        type = "info";
      }
    }

    if (!title) {
      if (/maximum|limit|already in your cart|available quantity/i.test(msg)) {
        title = "Quantity Limit Reached";
      } else if (type === "warning") {
        title = "Cart Notice";
      } else if (type === "error") {
        title = "Unable to Update Cart";
      } else if (type === "success") {
        title = "Success";
      } else {
        title = "Notice";
      }
    }

    return { type, title };
  }

  /**
   * Alert modal — replaces native window.alert()
   * @param {string|object} options
   * @returns {Promise<void>}
   */
  function alert(options) {
    return new Promise((resolve) => {
      let message = "";
      let title = "";
      let type = "";
      let primaryBtnText = "Got it";
      let secondaryBtnText = "";
      let onSecondary = null;

      if (typeof options === "string") {
        message = options;
      } else if (options && typeof options === "object") {
        message = options.message || "";
        title = options.title || "";
        type = options.type || "";
        primaryBtnText = options.primaryBtnText || options.buttonText || "Got it";
        secondaryBtnText = options.secondaryBtnText || "";
        onSecondary = options.onSecondary || null;
      }

      const detected = detectTypeAndTitle(message, title, type);
      title = detected.title;
      type = detected.type;

      const isCartRelated = /cart|quantity|limit/i.test(message);
      if (isCartRelated && !secondaryBtnText && window.tkCart) {
        secondaryBtnText = "View Cart";
        onSecondary = () => {
          if (window.tkCart?.open) window.tkCart.open();
        };
      }

      document.getElementById("tk-dialog-modal")?.remove();

      const modal = document.createElement("div");
      modal.id = "tk-dialog-modal";
      modal.className = "tk-modal";
      modal.innerHTML = `
        <div class="tk-modal__backdrop" data-tk-modal-close></div>
        <div class="tk-modal__card" role="alertdialog" aria-modal="true" aria-labelledby="tk-dialog-heading" aria-describedby="tk-dialog-desc">
          <div class="tk-modal__header">
            <div class="tk-modal__badge tk-modal__badge--${type}">
              ${ICONS[type] || ICONS.info}
            </div>
            <div class="tk-modal__titles">
              <h3 id="tk-dialog-heading" class="tk-modal__heading">${escapeHtml(title)}</h3>
              <p id="tk-dialog-desc" class="tk-modal__subheading">${escapeHtml(message)}</p>
            </div>
            <button type="button" class="tk-modal__close-btn" data-tk-modal-close aria-label="Close notice">
              <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2"><path d="M18 6 6 18M6 6l12 12"/></svg>
            </button>
          </div>
          <div class="tk-modal__footer ${secondaryBtnText ? 'tk-modal__footer--split' : ''}">
            ${secondaryBtnText ? `<button type="button" class="tk-modal__btn-secondary" data-tk-modal-secondary>${escapeHtml(secondaryBtnText)}</button>` : ''}
            <button type="button" class="tk-modal__btn-primary" data-tk-modal-primary>${escapeHtml(primaryBtnText)}</button>
          </div>
        </div>
      `;

      document.body.appendChild(modal);
      requestAnimationFrame(() => modal.classList.add("is-visible"));

      let done = false;
      const finish = () => {
        if (done) return;
        done = true;
        modal.classList.remove("is-visible");
        setTimeout(() => modal.remove(), 220);
        resolve();
      };

      modal.querySelector("[data-tk-modal-primary]").addEventListener("click", finish);
      if (secondaryBtnText) {
        modal.querySelector("[data-tk-modal-secondary]").addEventListener("click", () => {
          finish();
          if (onSecondary) onSecondary();
        });
      }
      modal.querySelectorAll("[data-tk-modal-close]").forEach((el) => el.addEventListener("click", finish));

      const onEsc = (e) => {
        if (e.key === "Escape") {
          document.removeEventListener("keydown", onEsc);
          finish();
        }
      };
      document.addEventListener("keydown", onEsc);

      setTimeout(() => {
        modal.querySelector("[data-tk-modal-primary]")?.focus();
      }, 50);
    });
  }

  /**
   * Confirm modal — replaces native window.confirm()
   * @param {string|object} options
   * @returns {Promise<boolean>}
   */
  function confirm(options) {
    return new Promise((resolve) => {
      let message = "";
      let title = "Please Confirm";
      let type = "info";
      let confirmText = "Confirm";
      let cancelText = "Cancel";

      if (typeof options === "string") {
        message = options;
      } else if (options && typeof options === "object") {
        message = options.message || "";
        title = options.title || "Please Confirm";
        type = options.type || "info";
        confirmText = options.confirmText || "Confirm";
        cancelText = options.cancelText || "Cancel";
      }

      document.getElementById("tk-dialog-modal")?.remove();

      const modal = document.createElement("div");
      modal.id = "tk-dialog-modal";
      modal.className = "tk-modal";
      modal.innerHTML = `
        <div class="tk-modal__backdrop" data-tk-modal-close></div>
        <div class="tk-modal__card" role="dialog" aria-modal="true" aria-labelledby="tk-dialog-heading" aria-describedby="tk-dialog-desc">
          <div class="tk-modal__header">
            <div class="tk-modal__badge tk-modal__badge--${type}">
              ${ICONS[type] || ICONS.info}
            </div>
            <div class="tk-modal__titles">
              <h3 id="tk-dialog-heading" class="tk-modal__heading">${escapeHtml(title)}</h3>
              <p id="tk-dialog-desc" class="tk-modal__subheading">${escapeHtml(message)}</p>
            </div>
            <button type="button" class="tk-modal__close-btn" data-tk-modal-close aria-label="Close dialog">
              <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2"><path d="M18 6 6 18M6 6l12 12"/></svg>
            </button>
          </div>
          <div class="tk-modal__footer tk-modal__footer--split">
            <button type="button" class="tk-modal__btn-secondary" data-tk-modal-cancel>${escapeHtml(cancelText)}</button>
            <button type="button" class="tk-modal__btn-primary" data-tk-modal-confirm>${escapeHtml(confirmText)}</button>
          </div>
        </div>
      `;

      document.body.appendChild(modal);
      requestAnimationFrame(() => modal.classList.add("is-visible"));

      let done = false;
      const finish = (result) => {
        if (done) return;
        done = true;
        modal.classList.remove("is-visible");
        setTimeout(() => modal.remove(), 220);
        resolve(result);
      };

      modal.querySelector("[data-tk-modal-confirm]").addEventListener("click", () => finish(true));
      modal.querySelector("[data-tk-modal-cancel]").addEventListener("click", () => finish(false));
      modal.querySelectorAll("[data-tk-modal-close]").forEach((el) => el.addEventListener("click", () => finish(false)));

      const onEsc = (e) => {
        if (e.key === "Escape") {
          document.removeEventListener("keydown", onEsc);
          finish(false);
        }
      };
      document.addEventListener("keydown", onEsc);

      setTimeout(() => {
        modal.querySelector("[data-tk-modal-confirm]")?.focus();
      }, 50);
    });
  }

  /**
   * Addon protection packages prompt modal
   * @param {Array} addons
   * @param {number} addonTotal
   * @returns {Promise<boolean>}
   */
  function askAddon(addons, addonTotal) {
    return new Promise((resolve) => {
      document.getElementById("tk-addon-modal")?.remove();
      const modal = document.createElement("div");
      modal.id = "tk-addon-modal";
      modal.className = "tk-modal";
      modal.innerHTML = `
        <div class="tk-modal__backdrop" data-tk-modal-close></div>
        <div class="tk-modal__card" role="dialog" aria-modal="true" aria-labelledby="tk-modal-heading">
          <div class="tk-modal__header">
            <div class="tk-modal__badge tk-modal__badge--info">
              ${ICONS.shield}
            </div>
            <div class="tk-modal__titles">
              <h3 id="tk-modal-heading" class="tk-modal__heading">Add Protection Packages?</h3>
              <p class="tk-modal__subheading">Your cart already includes packages for this model. Would you like to cover this additional device too?</p>
            </div>
            <button type="button" class="tk-modal__close-btn" data-tk-modal-close aria-label="Close dialog">
              <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2"><path d="M18 6 6 18M6 6l12 12"/></svg>
            </button>
          </div>
          <div class="tk-modal__packages">
            ${(addons || []).map((a) => `
              <div class="tk-modal__package-item">
                <div class="tk-modal__package-name">
                  <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="#059669" stroke-width="2" aria-hidden="true"><path d="M20 6 9 17l-5-5"/></svg>
                  <span>${escapeHtml(a.item.product_title)}</span>
                </div>
                <strong class="tk-modal__package-price">+${fmtMoney(Math.round(a.item.final_line_price / a.item.quantity))}</strong>
              </div>
            `).join("")}
          </div>
          <div class="tk-modal__footer">
            <button type="button" class="tk-modal__btn-primary" data-tk-modal-yes>Add with packages (+${fmtMoney(addonTotal)})</button>
            <button type="button" class="tk-modal__btn-secondary" data-tk-modal-no>Add device only</button>
          </div>
        </div>`;

      document.body.appendChild(modal);
      requestAnimationFrame(() => modal.classList.add("is-visible"));

      let done = false;
      const finish = (result) => {
        if (done) return;
        done = true;
        modal.classList.remove("is-visible");
        setTimeout(() => modal.remove(), 220);
        resolve(result);
      };

      modal.querySelector("[data-tk-modal-yes]").addEventListener("click", () => finish(true));
      modal.querySelector("[data-tk-modal-no]").addEventListener("click", () => finish(false));
      modal.querySelectorAll("[data-tk-modal-close]").forEach((el) => el.addEventListener("click", () => finish(false)));

      const onEsc = (e) => {
        if (e.key === "Escape") {
          document.removeEventListener("keydown", onEsc);
          finish(false);
        }
      };
      document.addEventListener("keydown", onEsc);
    });
  }

  // Intercept native window.alert so no unstyled browser dialog ever displays
  try {
    window.alert = function (msg) {
      alert(msg);
    };
  } catch (e) {
    console.warn("Could not patch window.alert:", e);
  }

  window.tkModal = {
    alert,
    confirm,
    askAddon,
    showNotice: alert,
  };
})();
